/**
 * Phase 2b — /docs/{missing-slug} 404 surface.
 *
 * Renders a stark, intentional empty state with "Did you mean?" suggestions
 * pulled from the live nav.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import DocsLayout from "../../components/docs/DocsLayout";
import useDocumentTitle from "../../hooks/useDocumentTitle";


function bestSuggestions(navLeaves, attemptedSlug, limit = 4) {
  if (!attemptedSlug || !navLeaves || navLeaves.length === 0) {
    return navLeaves.slice(0, limit);
  }
  const target = attemptedSlug.toLowerCase();
  // Score by simple substring + shared-prefix overlap.
  const scored = navLeaves.map((leaf) => {
    const slug = (leaf.slug || "").toLowerCase();
    let score = 0;
    if (slug === target) score += 100;
    if (slug.includes(target)) score += 30;
    if (target.includes(slug.split("/").pop() || "")) score += 10;
    // shared-prefix
    let prefix = 0;
    for (let i = 0; i < Math.min(slug.length, target.length); i += 1) {
      if (slug[i] === target[i]) prefix += 1;
      else break;
    }
    score += prefix;
    return { leaf, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.leaf);
}


export default function DocsNotFound({ attemptedSlug, navLeaves = [] }) {
  useDocumentTitle("Not found — QorTrace Docs");
  const suggestions = bestSuggestions(navLeaves, attemptedSlug, 4);

  return (
    <DocsLayout currentSlug={null}>
      <div className="qt-docs-404" data-testid="docs-404">
        <div
          className="qt-docs-eyebrow"
          style={{ color: "var(--qt-cyan)", marginBottom: "1rem" }}
        >
          404 // Not found
        </div>
        <h1 className="qt-docs-h1" data-testid="docs-404-title">
          That page doesn't exist (yet).
        </h1>
        <p
          className="text-base mt-4 max-w-md mx-auto"
          style={{ color: "#8B9BB4" }}
        >
          {attemptedSlug
            ? <>We couldn't find a doc at <code className="qt-docs-inline-code">/docs/{attemptedSlug}</code>. It may have been renamed or hasn't been published yet.</>
            : "We couldn't find that doc. It may have been renamed or hasn't been published yet."}
        </p>

        {suggestions.length > 0 && (
          <div className="mt-8 w-full max-w-lg mx-auto" data-testid="docs-404-suggestions">
            <div
              className="qt-docs-eyebrow text-left mb-4"
              style={{ color: "#4B5B76" }}
            >
              Did you mean…?
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {suggestions.map((leaf) => (
                <Link
                  key={leaf.slug}
                  to={`/docs/${leaf.slug}`}
                  className="qt-docs-tile group p-4"
                  data-testid={`docs-404-suggestion-${leaf.slug.replace(/\//g, "-")}`}
                >
                  <div className="flex items-center justify-between">
                    <div
                      className="font-heading font-semibold"
                      style={{ color: "var(--qt-text-1)" }}
                    >
                      {leaf.title}
                    </div>
                    <ArrowRight size={14} className="qt-docs-tile-arrow" />
                  </div>
                  <div
                    className="text-xs font-mono mt-1"
                    style={{ color: "#4B5B76" }}
                  >
                    /docs/{leaf.slug}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          <Link
            to="/docs"
            data-testid="docs-404-browse-all"
            className="inline-flex items-center gap-2 px-4 py-2 font-mono text-sm"
            style={{
              background: "var(--qt-cyan)",
              color: "#001114",
              fontWeight: 600,
            }}
          >
            Browse all docs <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </DocsLayout>
  );
}
